import { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from ".";
import { XIcon } from "lucide-react";
import { cn } from "@eaigle/utils/cn";

type SheetSide = "left" | "right";

type SheetProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  side?: SheetSide;
  headerExtra?: ReactNode;
  subHeader?: ReactNode;
  children: ReactNode;
  width?: number;
};

/**
 * Simple sheet (slide-in panel) without Radix.
 * Opens from left or right based on side. Renders in a portal.
 */
export function Sheet({
  open,
  onClose,
  title,
  side = "right",
  headerExtra,
  subHeader,
  children,
  width,
}: SheetProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }

    // Keep the first rendered frame off-screen so the browser always has a
    // transform to animate from, including when a route mounts the sheet.
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-100 flex pointer-events-auto">
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        className={cn(
          "top-2 bottom-2 fixed rounded-2xl z-101 w-full flex flex-col bg-white shadow-xl transition-transform duration-200 ease-out will-change-transform transform-gpu",
          {
            "max-w-md": width === undefined,
            "left-2": side === "left",
            "right-2": side === "right",
            "translate-x-0": mounted,
            "-translate-x-full": side === "left" && !mounted,
            "translate-x-full": side === "right" && !mounted,
          },
        )}
        style={width !== undefined ? { maxWidth: width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col border-b border-gray-200">
          <div className="flex items-center justify-between gap-2 p-4">
            <h2 id="sheet-title" className="text-lg font-semibold text-black shrink-0">
              {title}
            </h2>
            <div className="flex items-center gap-1.5 shrink-0">
              {headerExtra}
              <Button
                variant="ghost"
                onClick={onClose}
                className="p-1 hover:bg-black/10 rounded-md"
                aria-label="Close"
              >
                <XIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
          {subHeader && <div className="px-4 pb-4">{subHeader}</div>}
        </div>
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
